// hooks/useStudents.js

import { useState, useEffect } from "react";
import { deleteApi, getApi, putApi } from "../../services/api";
import { toast } from "react-toastify";

export default function useStudents() {
	const [students, setStudents] = useState([]);
	const [filteredStudents, setFilteredStudents] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [modalType, setModalType] = useState("");
	const [formType, setFormType] = useState("");
	const [selectedStudent, setSelectedStudent] = useState(null);
	const [enrollmentData, setEnrollmentData] = useState([]);
	const [enrolledCourses] = useState([]);
	const [form, setForm] = useState(null);

	const fetchEnrollmentData = async (userId) => {
		try {
			if (!userId) {
				throw new Error("User ID is required");
			}

			const response = await getApi(`/enrollment/${userId}`);

			setEnrollmentData(response);
		} catch (error) {
			toast.error("Error fetching enrollment details:", error.message);
		}
	};

	// Fetch students data from the database
	useEffect(() => {
		const fetchStudents = async () => {
			try {
				const response = await getApi(`/users`, { role: "student" });

				if (response.error) {
					throw new Error(response.error);
				}

				setStudents(response); // Set fetched Students
				setFilteredStudents(response); // Update filtered Students
			} catch (error) {
				console.error("Failed to fetch students", error);
			}
		};

		fetchStudents();
	}, []);

	// Update filtered students based on the search term
	useEffect(() => {
		if (searchTerm === "") {
			setFilteredStudents(students);
		} else {
			setFilteredStudents(
				students.filter((student) =>
					student.fullName
						.toLowerCase()
						.includes(searchTerm.toLowerCase())
				)
			);
		}
	}, [searchTerm, students]);

	const showModal = (type, student) => {
		setModalType(type);
		setSelectedStudent(student); // Update the selected student state with the fetched data

		if (type === "view" && student) {
			fetchEnrollmentData(student._id);
		} else if (type === "edit" && student) {
			// Ensure that availableTimes are set for viewing
			form?.setFieldsValue({
				...student,
			});
		} else {
			form?.resetFields();
		}

		setIsModalVisible(true); // Show the modal
		toast.info(`Opening ${type} modal for ${student?.fullName}`);
	};

	const handleModalConfirm = async (values) => {
		try {
			let response;

			if (modalType === "edit") {
				response = await putApi(`/users/${selectedStudent._id}`, {
					fullName: values.fullName,
					email: values.email,
					phoneNumber: values.phoneNumber,
				});
			} else if (modalType === "delete") {
				response = await deleteApi(`/users/${selectedStudent._id}`);
			}

			if (response && !response.error) {
				if (modalType === "edit") {
					setStudents((prev) =>
						prev.map((student) =>
							student._id === selectedStudent._id
								? response
								: student
						)
					);
					setFilteredStudents((prev) =>
						prev.map((student) =>
							student._id === selectedStudent._id
								? response
								: student
						)
					);
					toast.success("Student details updated successfully!");
				} else if (modalType === "delete") {
					setStudents((prev) =>
						prev.filter(
							(student) => student._id !== selectedStudent._id
						)
					);
					setFilteredStudents((prev) =>
						prev.filter(
							(student) => student._id !== selectedStudent._id
						)
					);
					toast.success("Student deleted successfully!");
				}
			} else {
				console.error(
					"Error handling modal action:",
					response?.message
				);
				toast.error(`Error: ${response?.message || "Unknown error"}`);
			}
		} catch (error) {
			toast.error("Error handling modal action:", error.message);
		}

		setIsModalVisible(false);
	};

	const handleModalCancel = () => {
		setIsModalVisible(false);
	};

	return {
		searchTerm,
		setSearchTerm,
		isModalVisible,
		setIsModalVisible,
		modalType,
		setModalType,
		formType,
		setFormType,
		selectedStudent,
		setSelectedStudent,
		enrollmentData,
		setEnrollmentData,
		form,
		setForm,
		students,
		setStudents,
		filteredStudents,
		enrolledCourses,
		showModal,
		handleModalConfirm,
		handleModalCancel,
	};
}
